/* eslint-disable */
import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { Link } from '@telus/koodo-ui-kit'
import Sites from '@sites/index'
import { getCookie } from '@apps/utils/common'
import { ACKNOWLEDGE_COOKIE_NAME } from '@/_engine/hooks/useCookieConsent'

// Routes where the "Manage Cookies" floating link should not render.
// Plans page (Figma G1) intentionally has no Manage Cookies entry; the koodo
// PlanCardFooter owns the sticky bottom shelf and there's no cookie link slot.
// Process Loader is a transient interstitial — design omits the cookie link
// while activation is processing. Already Activated (Figma H1/HH1) has no
// cookie link either.
const HIDE_COOKIE_MANAGER_ROUTES = [
  /\/activation\/plans\/?$/,
  /\/activation\/process-loader\/?$/,
  /\/activation\/already-activated\/?$/
]

const FloatingCookieManager = ({ 
  onManageCookies,
  showCookieModal,
  showCookieConsentBanner,
  cookieControllers,
  setCookieControllers 
}) => {
  const [isVisible, setIsVisible] = useState(false)
  const [hasPaymentSummary, setHasPaymentSummary] = useState(false)
  const [paymentSummaryHeight, setPaymentSummaryHeight] = useState(0)
  const [cookieManagerAnchor, setCookieManagerAnchor] = useState(null)
  
  // Get data from Redux store - separated for better readability
  const commonState = useSelector((state) => state.common)
  const { pathname } = useLocation()
  const isHiddenRoute = HIDE_COOKIE_MANAGER_ROUTES.some((re) => re.test(pathname))

  // Extract userType
  const userType = commonState.userType
  
  // Extract cookieContent with safe fallback
  const cookieContent = commonState.content?.cookieContent || {}
  
  // Extract selectedLanguage with safe fallback
  const selectedLanguage = commonState.user?.selectedLanguage || 'en'
  const isFrenchSelected = selectedLanguage.toLowerCase().startsWith('fr')
  

  // Check for PaymentSummary presence and measure height
  useEffect(() => {
    let observedPaymentSummary = null

    // Also listen for resize events (for Show/Hide button)
    const resizeObserver = new ResizeObserver(() => {
      checkForPaymentSummary()
    })

    const checkForPaymentSummary = () => {
      const anchor = document.querySelector('[data-component="payment-summary-cookie-anchor"]')
      setCookieManagerAnchor(anchor)

      // Look for PaymentSummary by data-component attribute
      const paymentSummary = document.querySelector('[data-component="payment-summary"]')
      if (observedPaymentSummary !== paymentSummary) {
        if (observedPaymentSummary) {
          resizeObserver.unobserve(observedPaymentSummary)
        }

        observedPaymentSummary = paymentSummary

        if (observedPaymentSummary) {
          resizeObserver.observe(observedPaymentSummary)
        }
      }

      if (paymentSummary) {
        setHasPaymentSummary(true)
        // Get the actual height of the PaymentSummary
        const height = paymentSummary.offsetHeight || paymentSummary.clientHeight
        setPaymentSummaryHeight(height)
      } else {
        setHasPaymentSummary(false)
        setPaymentSummaryHeight(0)
      }
    }

    // Initial check
    checkForPaymentSummary()

    // Set up observer to watch for PaymentSummary appearing/disappearing and size changes
    const observer = new MutationObserver(() => {
      checkForPaymentSummary()
    })

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style']
    })

    return () => {
      observer.disconnect()
      resizeObserver.disconnect()
    }
  }, [])

  // Visibility logic - simplified
  useEffect(() => {
    // Hide on routes where the design omits a Manage Cookies entry
    // (e.g. /activation/plans — Figma G1).
    if (isHiddenRoute) {
      setIsVisible(false)
      return
    }

    // Only show for consumer users
    if (userType !== Sites.consumer) {
      setIsVisible(false)
      return
    }

    // Hide if cookie modal is open
    if (showCookieModal) {
      setIsVisible(false)
      return
    }

    // Hide if cookie consent banner is showing (prop)
    if (showCookieConsentBanner) {
      setIsVisible(false)
      return
    }

    // Check DOM for cookie banner presence
    const checkForCookieBanner = () => {
      // Primary check: Look for cookie banner by data attribute
      const cookieBanner = document.querySelector('[data-footer-type="cookie-banner"]')
      
      // Check if banner exists and is visible
      if (cookieBanner && cookieBanner.offsetHeight > 0) {
        setIsVisible(false)
        return true
      }
      
      // No banner found, show the floating button
      setIsVisible(true)
      return false
    }

    // Initial check
    checkForCookieBanner()
    
    // Set up observer to watch for cookie banner appearing/disappearing
    const observer = new MutationObserver(() => {
      checkForCookieBanner()
    })
    
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style']
    })
    
    return () => {
      observer.disconnect()
    }
  }, [userType, showCookieModal, showCookieConsentBanner, isHiddenRoute])

  const handleManageCookies = () => {
    
    // If parent provided onManageCookies handler, use it
    if (onManageCookies) {
      onManageCookies()
      return
    }
    
    // Otherwise, try to handle it locally (backward compatibility)
    if (setCookieControllers && cookieControllers) {
      // Load current cookie consent state
      const cookieValue = getCookie(ACKNOWLEDGE_COOKIE_NAME)
      if (cookieValue) {
        try {
          const consent = JSON.parse(cookieValue)
          // console.log('🍪 Loading cookie consent for modal:', consent)
          
          // Update controllers with actual cookie values
          setCookieControllers(prev => prev.map(category => {
            const isGranted = category.required || 
                            (category.target && category.target.length > 0 && 
                             category.target.every(key => consent[key] === 'granted'))
            
            // console.log(`Setting ${category.name}: ${isGranted ? 'ON' : 'OFF'}`)
            return {
              ...category,
              granted: isGranted
            }
          }))
        } catch (e) {
          console.error('Failed to load cookie consent:', e)
        }
      }
    }
    
    console.warn('No way to open modal - onManageCookies prop not provided')
  }

  // Don't render if not visible
  if (!isVisible) {
    // console.log('🚫 FloatingCookieManager NOT rendering - isVisible is false')
    return null
  }

  // Get button config from cookieContent
  const footerManageCookiesButton = cookieContent?.[selectedLanguage || 'en']?.buttons?.find(
    btn => btn.id === 'footer_manage_cookies_label'
  )
  
  // console.log('🔘 Button found:', footerManageCookiesButton)
  
  const buttonLabel = footerManageCookiesButton?.buttonLabel || 'Manage Cookies'
  const displayButtonLabel = !isFrenchSelected && buttonLabel === 'Manage cookies' ? 'Manage Cookies' : buttonLabel
  const buttonAriaLabel = footerManageCookiesButton?.ariaLabel || 'Manage cookie preferences'

  const dynamicBottomHeight = paymentSummaryHeight === 132 ? paymentSummaryHeight + 6 : paymentSummaryHeight - 29
  const isFixedToPaymentSummary = hasPaymentSummary && !cookieManagerAnchor

  // Anchor opts into plain (regular) weight via `data-cookie-link-variant="plain"`.
  // Default is bold — used on the Intro page and the fallback floating render.
  const isPlainVariant = cookieManagerAnchor?.dataset?.cookieLinkVariant === 'plain'

  // Anchor opts into a larger 18px / 28px link via `data-cookie-link-size="large"`
  // (Verify page only). Default is 16px / 24px. blockLineHeight is a multiplier
  // (UDS computes lineHeight = multiplier × fontSize), so 28/18 yields 28px.
  const isLargeText = cookieManagerAnchor?.dataset?.cookieLinkSize === 'large'
  
  // Three rendering modes:
  // 1. fixed-to-payment-summary — sticky bottom-right above the payment bar
  // 2. portaled into an explicit anchor div (e.g. CheckoutLayout's footer) —
  //    let the anchor's parent control positioning; render the button bare.
  // 3. fallback — centered inline at the bottom of the route content.
  const renderClassName = isFixedToPaymentSummary
    ? 'fixed bottom-0 right-0 z-50 pr-4 sm:pr-6'
    : cookieManagerAnchor
      ? ''
      : 'w-full py-4'
  const buttonWrapperClassName = (isFixedToPaymentSummary || cookieManagerAnchor)
    ? ''
    : 'flex justify-center'

  const cookieManager = (
    <div
      data-footer-type="cookie-manager"
      className={renderClassName}
      style={isFixedToPaymentSummary ? { bottom: `${dynamicBottomHeight}px` } : {}}
    >
      <div className={buttonWrapperClassName}>
        <Link
          className="kpre-nowrap-link"
          onClick={handleManageCookies}
          accessibilityLabel={buttonAriaLabel}
          tokens={{
            blockFontSize: isLargeText ? 18 : 16,
            blockFontWeight: isPlainVariant ? '400' : '700',
            blockLineHeight: isLargeText ? 28 / 18 : 1.5,
            color: 'var(--color-copy)'
          }}
        >
          {displayButtonLabel}
        </Link>
      </div>
    </div>
  )

  return cookieManagerAnchor ? createPortal(cookieManager, cookieManagerAnchor) : cookieManager
}


export default FloatingCookieManager
